import { useLocation } from '@reach/router';
import { Link } from 'gatsby';
import React from 'react';
import MenuDown from 'assets/svg/menu-down.svg';

type SubMenuProps = {
  subMenuItems: any[]; // Replace 'any[]' with a more specific type if available
  nav: any; // Replace 'any' with a more specific type if available
  handleNavClick: () => void;
  activeNav: string;
  setActiveNav: React.Dispatch<React.SetStateAction<string>>;
};

const SubMenu: React.FC<SubMenuProps> = ({
  subMenuItems,
  nav,
  handleNavClick,
  activeNav,
  setActiveNav,
}) => {
  const [openSubMenus, setOpenSubMenus] = React.useState<{
    [key: string]: boolean;
  }>({});

  const toggleSubMenu = (title: string) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSubMenuClick = () => {
    handleNavClick();
    setActiveNav('');
    setOpenSubMenus({});
  };

  const isActive = activeNav === nav.title;

  return (
    <>
      <button
        className={`header-nav-item-link${isActive ? ' active' : ''}`}
        onClick={() => {
          setActiveNav(isActive ? '' : nav.title);
        }}
        type="button"
      >
        {nav.title}
        <MenuDown />
      </button>
      {isActive && (
        <div className="header-sub-nav">
          <Link
            to={`/${nav.link}`}
            className="header-nav-item-link"
            onClick={handleSubMenuClick}
          >
            <strong>{nav.title}</strong>
          </Link>
          <ul>
            {subMenuItems.map((item: any) => {
              const isSubOpen = !!openSubMenus[item.title];
              const hasChildren = item.strapi_children?.length > 0;

              return (
                <li key={item.title} className="header-nav-item">
                  {hasChildren ? (
                    <>
                      <button
                        className={`header-nav-item-link${
                          isSubOpen ? ' active' : ''
                        }`}
                        onClick={() => toggleSubMenu(item.title)}
                        type="button"
                      >
                        {item.title}
                        <MenuDown />
                      </button>
                      {isSubOpen && (
                        <ul className="header-sub-sub-nav">
                          <li>
                            <Link
                              to={`/${nav.link}/${item.slug}`}
                              className="header-nav-item-link"
                              onClick={handleSubMenuClick}
                            >
                              {item.title}
                            </Link>
                          </li>
                          {item.strapi_children.map((child: any) => (
                            <li
                              key={child.title}
                              className="header-nav-sub-item"
                            >
                              <Link
                                to={`/${nav.link}/${item.slug}/${child.slug}`}
                                className="header-nav-item-link"
                                onClick={handleSubMenuClick}
                              >
                                {child.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={`/${nav.link}/${item.slug}`}
                      className="header-nav-item-link"
                      onClick={handleSubMenuClick}
                    >
                      {item.title}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

type HeaderNavProps = {
  headerLinks: any[]; // Replace 'any[]' with a more specific type if available
  allnodes: any[]; // Replace 'any[]' with a more specific type if available
  handleNavClick: () => void;
};

export const HeaderNav: React.FC<HeaderNavProps> = ({
  headerLinks,
  allnodes,
  handleNavClick,
}) => {
  const [activeNav, setActiveNav] = React.useState('');
  const location = useLocation();
  const navRef = React.useRef<HTMLUListElement | null>(null);
  React.useEffect(() => {
    setActiveNav('');
  }, [location.pathname]);
  // Close on outside click
  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveNav('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  return (
    <ul className="header-nav" ref={navRef}>
      {headerLinks.map((nav: any) => {
        const pageNode = allnodes.find((node: any) => node.slug === nav.link);
        const hasChildren = pageNode.strapi_children.length;
        return (
          <li className="header-nav-item" key={nav.title}>
            {hasChildren ? (
              <SubMenu
                subMenuItems={pageNode.strapi_children}
                nav={nav}
                handleNavClick={handleNavClick}
                activeNav={activeNav}
                setActiveNav={setActiveNav}
              />
            ) : (
              <Link
                to={`/${nav.link}`}
                className="header-nav-item-link"
                onClick={() => {
                  handleNavClick();
                  setActiveNav('');
                }}
              >
                {nav.title}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
};
